import { Component, OnDestroy, computed, inject, signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { EMPTY, Subscription, catchError, of, switchMap, timer } from 'rxjs';
import { AccountService } from '../core/account.service';
import { AuthService } from '../core/auth.service';
import { DeskService } from '../core/desk.service';
import { formatMoney, formatPct, formatPrice, formatQty, formatWhen } from '../core/format';
import { errorMessage } from '../core/http-error';
import { Candle, MarketDetail, Quote } from '../core/models';
import { LanguageService } from '../core/language';
import { MarketService } from '../core/market.service';
import { CandleChart } from '../ui/candle-chart';

@Component({
  selector: 'app-portfolio',
  imports: [RouterLink, CandleChart],
  templateUrl: './portfolio.html',
})
export class PortfolioPage implements OnDestroy {
  protected readonly account = inject(AccountService);
  protected readonly auth = inject(AuthService);
  private readonly market = inject(MarketService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly desk = inject(DeskService);
  protected readonly lang = inject(LanguageService);
  protected readonly failed = signal<string | null>(null);
  protected readonly selected = signal('SPY');
  protected readonly detail = signal<MarketDetail | null>(null);
  protected readonly listTab = signal<'watch' | 'movers'>('watch');
  protected readonly pane = signal<'positions' | 'fills'>('positions');
  protected readonly orderQty = signal(1);
  protected readonly now = signal(new Date());
  protected readonly rail = signal<'deposit' | 'iq' | 'help' | 'bot' | 'jupiter' | 'trust' | null>(null);
  protected readonly trustState = signal<'opening' | 'connected' | 'closed' | 'missing'>('opening');
  protected readonly trustAccount = signal('');
  protected readonly trustHref = trustAppHref();
  private jupiterLoader: Promise<void> | null = null;
  private jupiterLive = false;
  protected readonly iqTab = signal<'stats' | 'analysis' | 'indicators'>('stats');
  protected readonly depositStep = signal<'account' | 'network' | 'pay'>('account');
  protected readonly depositOpen = signal<'account' | 'network' | null>(null);
  protected readonly depositTier = signal<'classic' | 'silver' | 'gold' | 'platinum' | 'vip' | null>(null);
  protected readonly depositCoin = signal<'BTC' | 'ETH' | 'SOL' | 'TRX' | null>(null);
  protected readonly depositAmount = signal('');
  protected readonly depositTraders = signal(0);
  protected readonly copied = signal(false);
  protected readonly tiers = computed(() => [
    { id: 'classic' as const, name: 'Classic', minimum: '250', label: '$250', line: this.lang.t('accClassicLine') },
    { id: 'silver' as const, name: 'Silver', minimum: '2500', label: '$2,500', line: this.lang.t('accSilverLine') },
    { id: 'gold' as const, name: 'Gold', minimum: '10000', label: '$10,000', line: this.lang.t('accGoldLine') },
    { id: 'platinum' as const, name: 'Platinum', minimum: '50000', label: '$50,000', line: this.lang.t('accPlatLine') },
    { id: 'vip' as const, name: 'VIP', minimum: '100000', label: '$100,000', line: this.lang.t('accVipLine') },
  ]);
  protected readonly coins = [
    { id: 'BTC' as const, name: 'Bitcoin', network: 'Bitcoin', address: 'bc1qc6rgs66hyzhw9euagu8r2rla3g287dxgs8ggns', qr: '/qr-btc.jpg' },
    { id: 'ETH' as const, name: 'Ethereum', network: 'Ethereum', address: '0x9F354d7e16Ae2A35A3BD6e22b2151f2c00839077', qr: '/qr-eth.jpg' },
    { id: 'SOL' as const, name: 'Solana', network: 'Solana', address: 'GvqeHYQAHwFPgeWVS4nfS4HnAGA3RkhBPKXngH8ynSm4', qr: '/qr-sol.jpg' },
    { id: 'TRX' as const, name: 'Tron', network: 'Tron', address: 'TEZiWnP3cCJKxjv6HkpB7rpqhtdYWyyqEo', qr: '/qr-trx.jpg' },
  ];
  protected readonly money = formatMoney;
  protected readonly price = formatPrice;
  protected readonly pct = formatPct;
  protected readonly qty = formatQty;
  protected readonly when = formatWhen;

  private readonly subscription = new Subscription();

  constructor() {
    this.subscription.add(
      timer(0, 1000).subscribe(() => this.now.set(new Date())),
    );

    this.subscription.add(
      timer(0, 4000)
        .pipe(
          switchMap(() =>
            this.account.load().pipe(
              catchError((error: unknown) => {
                this.failed.set(errorMessage(error, this.lang.t('poLoadFail')));
                return EMPTY;
              }),
            ),
          ),
        )
        .subscribe(() => {
          this.failed.set(null);
          const book = this.account.portfolio();
          const user = this.auth.user();
          if (book && user && book.cash !== user.cash) {
            this.auth.remember({ ...user, cash: book.cash });
          }
          const quotes = this.market.quotes();
          if (!quotes.length) {
            return;
          }
          const current = this.selected();
          if (!quotes.some((quote) => quote.symbol === current)) {
            this.choose(quotes[0].symbol);
          }
        }),
    );

    this.subscription.add(
      this.route.queryParamMap.subscribe((params) => {
        const symbol = (params.get('symbol') ?? '').trim().toUpperCase();
        if (symbol && symbol !== this.selected()) {
          this.selected.set(symbol);
          this.orderQty.set(symbol.includes('-') ? 0.1 : 1);
        }
      }),
    );

    this.subscription.add(
      toObservable(this.selected)
        .pipe(
          switchMap((symbol) =>
            timer(0, 2000).pipe(
              switchMap(() => this.market.detail(symbol).pipe(catchError(() => of(null)))),
            ),
          ),
        )
        .subscribe((detail) => {
          if (detail) {
            this.detail.set(detail);
          }
        }),
    );
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
    if (this.jupiterLive) {
      jupiterPlugin()?.close();
      this.jupiterLive = false;
    }
  }

  protected readonly watched = computed(() => this.market.quotes());

  protected readonly movers = computed(() =>
    [...this.market.quotes()].sort((a, b) => Math.abs(b.changePct) - Math.abs(a.changePct)).slice(0, 8),
  );

  protected readonly listed = computed(() => (this.listTab() === 'watch' ? this.watched() : this.movers()));

  protected readonly deposit = computed(() => this.coins.find((coin) => coin.id === this.depositCoin()) ?? null);

  protected readonly tier = computed(() => this.tiers().find((tier) => tier.id === this.depositTier()) ?? null);

  protected readonly active = computed(() => {
    const symbol = this.selected();
    return this.market.quotes().find((quote) => quote.symbol === symbol) ?? this.detail()?.quote ?? null;
  });

  protected readonly iq = computed(() => {
    this.lang.code();
    iqSay = (key) => this.lang.t(key);
    const quote = this.active();
    const candles = this.detail()?.candles ?? [];
    return readTape(candles, quote);
  });

  protected readonly ohlc = computed(() => {
    const quote = this.active();
    const candles = this.detail()?.candles ?? [];
    const last = candles[candles.length - 1];
    if (!quote || !last) {
      return null;
    }
    return { open: last.open, high: last.high, low: last.low, close: quote.price };
  });

  protected clock(): string {
    return this.now().toLocaleTimeString('en-GB', { hour12: false });
  }

  protected fixed(value: number | null, digits: number): string {
    return value === null ? '—' : value.toFixed(digits);
  }

  protected session(value: number | null, precision: number): string {
    return value === null ? '—' : formatPrice(value, precision);
  }

  protected openRail(panel: 'deposit' | 'iq' | 'help' | 'bot'): void {
    if (panel === 'deposit') {
      this.depositStep.set('account');
      this.depositOpen.set(null);
      this.depositTier.set(null);
      this.depositCoin.set(null);
      this.depositAmount.set('');
      this.depositTraders.set(0);
      this.copied.set(false);
    }
    this.rail.set(panel);
  }

  protected toggleDeposit(which: 'account' | 'network'): void {
    this.depositOpen.update((open) => (open === which ? null : which));
  }

  protected chooseTier(id: 'classic' | 'silver' | 'gold' | 'platinum' | 'vip'): void {
    const tier = this.tiers().find((item) => item.id === id);
    this.depositTier.set(id);
    this.depositAmount.set(tier?.minimum ?? '');
    this.depositOpen.set(null);
  }

  protected nextDeposit(): void {
    if (!this.depositTier()) {
      return;
    }
    this.depositCoin.set(null);
    this.copied.set(false);
    this.depositOpen.set(null);
    this.depositTraders.set(this.traderCount(this.depositTier()!));
    this.depositStep.set('network');
  }

  private traderCount(id: 'classic' | 'silver' | 'gold' | 'platinum' | 'vip'): number {
    const span = {
      classic: [18420, 24680],
      silver: [9120, 14140],
      gold: [4080, 6920],
      platinum: [1540, 2860],
      vip: [620, 1340],
    }[id];
    return span[0] + Math.floor(Math.random() * (span[1] - span[0] + 1));
  }

  protected backDeposit(): void {
    this.copied.set(false);
    this.depositOpen.set(null);
    if (this.depositStep() === 'pay') {
      this.depositCoin.set(null);
      this.depositStep.set('network');
      return;
    }
    this.depositCoin.set(null);
    this.depositStep.set('account');
  }

  protected chooseCoin(id: 'BTC' | 'ETH' | 'SOL' | 'TRX'): void {
    this.depositCoin.set(id);
    this.copied.set(false);
    this.depositOpen.set(null);
    this.depositStep.set('pay');
  }

  protected copyAddress(): void {
    const address = this.deposit()?.address;
    if (!address) {
      return;
    }
    const done = () => {
      this.copied.set(true);
      window.setTimeout(() => this.copied.set(false), 1600);
    };
    const fallback = () => {
      const area = document.createElement('textarea');
      area.value = address;
      area.setAttribute('readonly', '');
      area.style.position = 'fixed';
      area.style.left = '-9999px';
      document.body.appendChild(area);
      area.select();
      const ok = document.execCommand('copy');
      area.remove();
      if (ok) {
        done();
      }
    };
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(address).then(done).catch(fallback);
    } else {
      fallback();
    }
  }

  protected askDesk(message: string): void {
    this.rail.set(null);
    this.desk.askAbout(message);
  }

  protected openTrust(event: Event): void {
    if (isPhone()) {
      return;
    }
    event.preventDefault();
    if (this.jupiterLive) {
      jupiterPlugin()?.close();
      this.jupiterLive = false;
    }
    this.rail.set('trust');
    this.trustState.set('opening');
    this.trustAccount.set('');
    const provider = trustProvider();
    if (!provider) {
      this.trustState.set('missing');
      return;
    }
    provider.request({ method: 'eth_requestAccounts' }).then(
      (accounts) => {
        if (this.rail() !== 'trust') {
          return;
        }
        const first = Array.isArray(accounts) ? String(accounts[0] ?? '') : '';
        this.trustAccount.set(first);
        this.trustState.set(first ? 'connected' : 'closed');
      },
      () => {
        if (this.rail() === 'trust') {
          this.trustState.set('closed');
        }
      },
    );
  }

  protected openJupiter(): void {
    this.rail.set('jupiter');
    setTimeout(() => {
      void this.loadJupiter()
        .then(() => {
          if (this.rail() !== 'jupiter' || !document.getElementById('jupiter-plugin')) {
            return;
          }
          const plugin = jupiterPlugin();
          if (!plugin) {
            return;
          }
          if (this.jupiterLive) {
            plugin.close();
            this.jupiterLive = false;
          }
          plugin.init({
            displayMode: 'integrated',
            integratedTargetId: 'jupiter-plugin',
            containerStyles: { width: '100%', height: '640px' },
          });
          this.jupiterLive = true;
        })
        .catch(() => undefined);
    });
  }

  protected closeRail(): void {
    if (this.jupiterLive) {
      jupiterPlugin()?.close();
      this.jupiterLive = false;
    }
    this.rail.set(null);
  }

  private loadJupiter(): Promise<void> {
    if (jupiterPlugin()) {
      return Promise.resolve();
    }
    if (this.jupiterLoader) {
      return this.jupiterLoader;
    }
    this.jupiterLoader = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = 'https://plugin.jup.ag/plugin-v1.js';
      script.async = true;
      script.dataset['jupiter'] = '1';
      script.onload = () => resolve();
      script.onerror = () => {
        this.jupiterLoader = null;
        reject(new Error('Jupiter did not load'));
      };
      document.head.appendChild(script);
    });
    return this.jupiterLoader;
  }

  protected sparkPoints(): string {
    const points = this.active()?.spark ?? [];
    if (points.length < 2) {
      return '';
    }
    const min = Math.min(...points);
    const max = Math.max(...points);
    const span = max - min || 1;
    return points
      .map((point, index) => {
        const x = (index / (points.length - 1)) * 220;
        const y = 58 - ((point - min) / span) * 48;
        return `${x.toFixed(1)},${y.toFixed(1)}`;
      })
      .join(' ');
  }

  protected choose(symbol: string): void {
    this.selected.set(symbol);
    this.orderQty.set(symbol.includes('-') ? 0.1 : 1);
    const current = this.route.snapshot.queryParamMap.get('symbol');
    if (current?.toUpperCase() !== symbol) {
      void this.router.navigate([], {
        relativeTo: this.route,
        queryParams: { symbol },
        queryParamsHandling: 'merge',
        replaceUrl: true,
      });
    }
  }

  protected nudge(step: number): void {
    const symbol = this.selected();
    const size = symbol.includes('-') ? 0.1 : 1;
    const next = Math.round((this.orderQty() + step * size) * 10) / 10;
    this.orderQty.set(Math.max(size, next));
  }

  protected bid(quote: Quote): number {
    return this.touch(quote.price, 0.9998, quote.precision);
  }

  protected ask(quote: Quote): number {
    return this.touch(quote.price, 1.0002, quote.precision);
  }

  private touch(price: number, factor: number, precision: number): number {
    const scale = 10 ** precision;
    return Math.round(price * factor * scale) / scale;
  }
}

interface TapeRow {
  name: string;
  note: string;
  value: string;
}

let iqSay: (key: string) => string = (key) => key;

function iq(key: string, vars: Record<string, string> = {}): string {
  return iqSay(key).replace(/\{(\w+)\}/g, (_, name: string) => vars[name] ?? '');
}

interface JupiterPlugin {
  init: (props: {
    displayMode: 'integrated';
    integratedTargetId: string;
    containerStyles?: { width?: string; height?: string };
  }) => void;
  close: () => void;
}

interface Eip1193 {
  isTrust?: boolean;
  isTrustWallet?: boolean;
  providers?: Eip1193[];
  ethereum?: Eip1193;
  request: (args: { method: string }) => Promise<unknown>;
}

function isPhone(): boolean {
  const agent = navigator.userAgent;
  return /Android|iPhone|iPad|iPod/i.test(agent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
}

function trustAppHref(): string {
  if (/Android/i.test(navigator.userAgent)) {
    return 'intent://#Intent;scheme=trust;package=com.wallet.crypto.trustapp;S.browser_fallback_url=https%3A%2F%2Flink.trustwallet.com%2F;end';
  }
  return 'https://link.trustwallet.com/';
}

function trustProvider(): Eip1193 | undefined {
  const host = window as Window & { trustwallet?: Eip1193; ethereum?: Eip1193 };
  const trust = host.trustwallet;
  if (trust?.ethereum?.request) {
    return trust.ethereum;
  }
  if (trust?.request) {
    return trust;
  }
  const ethereum = host.ethereum;
  if (!ethereum) {
    return undefined;
  }
  const nested = ethereum.providers?.find((item) => item.isTrust || item.isTrustWallet);
  if (nested?.request) {
    return nested;
  }
  if (ethereum.isTrust || ethereum.isTrustWallet) {
    return ethereum;
  }
  return undefined;
}

function jupiterPlugin(): JupiterPlugin | undefined {
  return (window as Window & { Jupiter?: JupiterPlugin }).Jupiter;
}

interface TapeRead {
  score: number;
  tone: string;
  rsi: number | null;
  low: number | null;
  high: number | null;
  spot: number;
  analysis: string;
  stats: string;
  rows: TapeRow[];
}

function readTape(candles: Candle[], quote: Quote | null): TapeRead {
  const closes = candles.map((candle) => candle.close);
  const price = quote?.price ?? closes[closes.length - 1] ?? 0;
  const precision = quote?.precision ?? 2;
  const low = quote?.low ?? null;
  const high = quote?.high ?? null;
  const spot = low !== null && high !== null && high > low ? Math.min(1, Math.max(0, (price - low) / (high - low))) : 0.5;
  const rsiNow = wilderRsi(closes, 14);
  const rsiPrior = wilderRsi(closes.slice(0, -3), 14);
  const macd = macdRead(closes);
  const sma20 = simpleAverage(closes, 20);
  const sma50 = simpleAverage(closes, 50);
  const bands = bollinger(closes, 20);
  const stoch = stochastic(candles, 14);
  const range = averageTrueRange(candles, 14);
  const tone = tapeTone(rsiNow, price, sma20, sma50, macd?.histogram ?? null);
  const money = (value: number) => formatPrice(value, precision);
  const rows: TapeRow[] = [
    { name: iq('iqRsiName'), note: rsiNote(rsiNow), value: oscillator(rsiNow) },
    { name: iq('iqMacdName'), note: iq('iqMacdNote'), value: signed(macd?.line ?? null, precision) },
    { name: iq('iqSignalName'), note: iq('iqSignalNote'), value: signed(macd?.signal ?? null, precision) },
    { name: iq('iqHistName'), note: sentenceCase(histogramNote(macd)), value: signed(macd?.histogram ?? null, precision) },
    { name: iq('iqSma20Name'), note: priceVersus(price, sma20, iq('iqAvg20')), value: level(sma20, money) },
    { name: iq('iqSma50Name'), note: priceVersus(price, sma50, iq('iqAvg50')), value: level(sma50, money) },
    { name: iq('iqBollUpName'), note: iq('iqBollUpNote'), value: level(bands?.upper ?? null, money) },
    { name: iq('iqBollLoName'), note: iq('iqBollLoNote'), value: level(bands?.lower ?? null, money) },
    { name: iq('iqStochName'), note: stochNote(stoch), value: oscillator(stoch) },
    { name: iq('iqAtrName'), note: iq('iqAtrNote'), value: level(range, money) },
  ];
  return {
    score: rsiNow === null ? 50 : Math.round(rsiNow),
    tone,
    rsi: rsiNow,
    low,
    high,
    spot,
    analysis: analysisCopy(quote, price, precision, rsiNow, rsiPrior, macd, sma20, sma50, bands, stoch, range, low, high),
    stats: statsCopy(quote, price, sma20, sma50, low, high, spot, precision),
    rows,
  };
}

function analysisCopy(
  quote: Quote | null,
  price: number,
  precision: number,
  rsi: number | null,
  rsiPrior: number | null,
  macd: MacdRead | null,
  sma20: number | null,
  sma50: number | null,
  bands: { upper: number; lower: number } | null,
  stoch: number | null,
  range: number | null,
  low: number | null,
  high: number | null,
): string {
  if (!quote || rsi === null) {
    return iq('iqNoCandles');
  }
  const money = (value: number) => formatPrice(value, precision);
  const shift = rsiPrior === null ? '' : rsiShift(rsi, rsiPrior);
  const sentences = [
    iq('iqRsiLine', { symbol: quote.symbol, rsi: rsi.toFixed(1), shift, note: rsiSentence(rsi) }),
    macdSentence(macd, precision),
    averageSentence(price, sma20, sma50, money),
    bandSentence(price, bands, money),
    stoch === null ? '' : `${stochSentence(stoch)}`,
    range === null ? '' : iq('iqAtrLine', { range: money(range) }),
    sessionSentence(price, low, high, money),
  ];
  return sentences.filter((sentence) => sentence.length > 0).join(' ');
}

function statsCopy(
  quote: Quote | null,
  price: number,
  sma20: number | null,
  sma50: number | null,
  low: number | null,
  high: number | null,
  spot: number,
  precision: number,
): string {
  if (!quote) {
    return iq('iqSelect');
  }
  const money = (value: number) => formatPrice(value, precision);
  const place = low !== null && high !== null && high > low
    ? iq('iqPlace', { pct: String(Math.round(spot * 100)), low: money(low), high: money(high) })
    : '';
  return `${quote.name}. ${averageSentence(price, sma20, sma50, money)} ${place}`.trim();
}

function tapeTone(
  rsi: number | null,
  price: number,
  sma20: number | null,
  sma50: number | null,
  histogram: number | null,
): string {
  if (rsi !== null && rsi >= 70) {
    return iq('iqStretched');
  }
  if (rsi !== null && rsi <= 30) {
    return iq('iqWashed');
  }
  const above20 = sma20 !== null && price > sma20;
  const above50 = sma50 !== null && price > sma50;
  if (above20 && above50 && histogram !== null && histogram > 0) {
    return iq('iqFirm');
  }
  if (!above20 && sma20 !== null && !above50 && sma50 !== null && histogram !== null && histogram < 0) {
    return iq('iqSoft');
  }
  return iq('iqNeutral');
}

function rsiShift(rsi: number, prior: number): string {
  const gap = rsi - prior;
  if (gap >= 2) {
    return iq('iqShiftUp', { prior: prior.toFixed(1) });
  }
  if (gap <= -2) {
    return iq('iqShiftDown', { prior: prior.toFixed(1) });
  }
  return iq('iqShiftFlat', { prior: prior.toFixed(1) });
}

function rsiSentence(rsi: number): string {
  if (rsi >= 70) {
    return iq('iqRsiHigh');
  }
  if (rsi <= 30) {
    return iq('iqRsiLow');
  }
  return iq('iqRsiMid');
}

function rsiNote(rsi: number | null): string {
  if (rsi === null) {
    return iq('iqNeeds15');
  }
  if (rsi >= 70) {
    return iq('iqRsiStretchNote');
  }
  if (rsi <= 30) {
    return iq('iqRsiWashNote');
  }
  return iq('iqRsiMidNote');
}

function macdSentence(macd: MacdRead | null, precision: number): string {
  if (!macd) {
    return '';
  }
  const side = macd.line >= macd.signal ? iq('iqAbove') : iq('iqBelow');
  return iq('iqMacdLine', { line: signed(macd.line, precision), side, signal: signed(macd.signal, precision), hist: histogramNote(macd) });
}

function sentenceCase(text: string): string {
  return text.length === 0 ? text : text.charAt(0).toUpperCase() + text.slice(1);
}

function histogramNote(macd: MacdRead | null): string {
  if (!macd) {
    return iq('iqNeeds35');
  }
  const widening = Math.abs(macd.histogram) > Math.abs(macd.prior) + Math.abs(macd.line) * 0.002;
  const narrowing = Math.abs(macd.histogram) + Math.abs(macd.line) * 0.002 < Math.abs(macd.prior);
  if (macd.histogram > 0 && widening) {
    return iq('iqHistPosWide');
  }
  if (macd.histogram > 0 && narrowing) {
    return iq('iqHistPosNarrow');
  }
  if (macd.histogram < 0 && widening) {
    return iq('iqHistNegWide');
  }
  if (macd.histogram < 0 && narrowing) {
    return iq('iqHistNegNarrow');
  }
  if (macd.histogram > 0) {
    return iq('iqHistPos');
  }
  if (macd.histogram < 0) {
    return iq('iqHistNeg');
  }
  return iq('iqHistFlat');
}

function averageSentence(price: number, sma20: number | null, sma50: number | null, money: (value: number) => string): string {
  if (sma20 === null) {
    return '';
  }
  const versus20 = price >= sma20 ? iq('iqAbove') : iq('iqBelow');
  if (sma50 === null) {
    return iq('iqPrice20', { side: versus20, price: money(sma20) });
  }
  const versus50 = price >= sma50 ? iq('iqAbove') : iq('iqBelow');
  return iq('iqPrice2050', { a: versus20, p20: money(sma20), b: versus50, p50: money(sma50) });
}

function bandSentence(price: number, bands: { upper: number; lower: number } | null, money: (value: number) => string): string {
  if (!bands) {
    return '';
  }
  if (price > bands.upper) {
    return iq('iqBandUp', { price: money(bands.upper) });
  }
  if (price < bands.lower) {
    return iq('iqBandDown', { price: money(bands.lower) });
  }
  return iq('iqBandIn', { low: money(bands.lower), high: money(bands.upper) });
}

function stochSentence(stoch: number): string {
  if (stoch >= 80) {
    return iq('iqStochTop', { value: stoch.toFixed(1) });
  }
  if (stoch <= 20) {
    return iq('iqStochBot', { value: stoch.toFixed(1) });
  }
  return iq('iqStoch', { value: stoch.toFixed(1) });
}

function stochNote(stoch: number | null): string {
  if (stoch === null) {
    return iq('iqNeeds14');
  }
  if (stoch >= 80) {
    return iq('iqStochTopNote');
  }
  if (stoch <= 20) {
    return iq('iqStochBotNote');
  }
  return iq('iqStochMidNote');
}

function sessionSentence(price: number, low: number | null, high: number | null, money: (value: number) => string): string {
  if (low === null || high === null || high <= low) {
    return '';
  }
  const through = Math.round(((price - low) / (high - low)) * 100);
  return iq('iqSession', { above: money(price - low), under: money(high - price), pct: String(through) });
}

function priceVersus(price: number, average: number | null, label: string): string {
  if (average === null) {
    return iq('iqNeedsMore', { label });
  }
  return price >= average ? iq('iqAboveLabel', { label }) : iq('iqBelowLabel', { label });
}

function oscillator(value: number | null): string {
  return value === null ? '—' : value.toFixed(1);
}

function level(value: number | null, money: (value: number) => string): string {
  return value === null ? '—' : money(value);
}

function signed(value: number | null, precision: number): string {
  if (value === null) {
    return '—';
  }
  const digits = Math.max(precision, 2);
  const text = Math.abs(value).toLocaleString('en-US', { minimumFractionDigits: digits, maximumFractionDigits: digits });
  return value < 0 ? `−${text}` : text;
}

interface MacdRead {
  line: number;
  signal: number;
  histogram: number;
  prior: number;
}

function wilderRsi(closes: number[], period: number): number | null {
  if (closes.length <= period) {
    return null;
  }
  let gain = 0;
  let loss = 0;
  for (let index = 1; index <= period; index += 1) {
    const diff = closes[index] - closes[index - 1];
    if (diff >= 0) {
      gain += diff;
    } else {
      loss -= diff;
    }
  }
  let averageGain = gain / period;
  let averageLoss = loss / period;
  for (let index = period + 1; index < closes.length; index += 1) {
    const diff = closes[index] - closes[index - 1];
    const up = diff > 0 ? diff : 0;
    const down = diff < 0 ? -diff : 0;
    averageGain = (averageGain * (period - 1) + up) / period;
    averageLoss = (averageLoss * (period - 1) + down) / period;
  }
  if (averageLoss === 0) {
    return 100;
  }
  return 100 - 100 / (1 + averageGain / averageLoss);
}

function macdRead(closes: number[]): MacdRead | null {
  const fast = emaSeries(closes, 12);
  const slow = emaSeries(closes, 26);
  const line: number[] = [];
  for (let index = 0; index < closes.length; index += 1) {
    if (Number.isFinite(fast[index]) && Number.isFinite(slow[index])) {
      line.push(fast[index] - slow[index]);
    }
  }
  const signal = emaSeries(line, 9);
  const last = line.length - 1;
  if (last < 1 || !Number.isFinite(signal[last]) || !Number.isFinite(signal[last - 1])) {
    return null;
  }
  return {
    line: line[last],
    signal: signal[last],
    histogram: line[last] - signal[last],
    prior: line[last - 1] - signal[last - 1],
  };
}

function emaSeries(values: number[], period: number): number[] {
  const series = Array<number>(values.length).fill(Number.NaN);
  if (values.length < period) {
    return series;
  }
  let seed = 0;
  for (let index = 0; index < period; index += 1) {
    seed += values[index];
  }
  let previous = seed / period;
  series[period - 1] = previous;
  const weight = 2 / (period + 1);
  for (let index = period; index < values.length; index += 1) {
    previous = values[index] * weight + previous * (1 - weight);
    series[index] = previous;
  }
  return series;
}

function simpleAverage(values: number[], period: number): number | null {
  if (values.length < period) {
    return null;
  }
  let sum = 0;
  for (let index = values.length - period; index < values.length; index += 1) {
    sum += values[index];
  }
  return sum / period;
}

function bollinger(closes: number[], period: number): { upper: number; lower: number } | null {
  const middle = simpleAverage(closes, period);
  if (middle === null) {
    return null;
  }
  let square = 0;
  for (let index = closes.length - period; index < closes.length; index += 1) {
    const gap = closes[index] - middle;
    square += gap * gap;
  }
  const deviation = Math.sqrt(square / period) * 2;
  return { upper: middle + deviation, lower: middle - deviation };
}

function stochastic(candles: Candle[], period: number): number | null {
  if (candles.length < period) {
    return null;
  }
  const window = candles.slice(-period);
  let high = window[0].high;
  let low = window[0].low;
  for (const candle of window) {
    high = Math.max(high, candle.high);
    low = Math.min(low, candle.low);
  }
  if (high === low) {
    return 50;
  }
  return ((window[window.length - 1].close - low) / (high - low)) * 100;
}

function averageTrueRange(candles: Candle[], period: number): number | null {
  if (candles.length <= period) {
    return null;
  }
  const ranges: number[] = [];
  for (let index = 1; index < candles.length; index += 1) {
    const candle = candles[index];
    const previous = candles[index - 1].close;
    ranges.push(Math.max(candle.high - candle.low, Math.abs(candle.high - previous), Math.abs(candle.low - previous)));
  }
  let average = 0;
  for (let index = 0; index < period; index += 1) {
    average += ranges[index];
  }
  average /= period;
  for (let index = period; index < ranges.length; index += 1) {
    average = (average * (period - 1) + ranges[index]) / period;
  }
  return average;
}
